import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { useToast } from '@shared/hooks/use-toast';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { WalletApiAdapter, type WalletTransactionsFilters } from '../adapters/WalletApiAdapter';

type ExportFilters = Pick<WalletTransactionsFilters, 'advanced_filters' | 'sorting'>;

export const useExportWalletTransactions = (walletId?: number) => {
  const [isExporting, setIsExporting] = useState(false);
  const { toast } = useToast();
  const { t } = useTranslation('walletsModule');

  const apiAdapter = useMemo(() => new WalletApiAdapter(createHttpClient(apiClientWH)), []);

  const exportWalletTransactions = async (filters: ExportFilters = {}) => {
    if (typeof walletId !== 'number' || Number.isNaN(walletId)) return;

    setIsExporting(true);
    try {
      const response = await apiAdapter.exportWalletTransactions(walletId, filters);

      if (response.url_file) {
        window.open(response.url_file, '_blank');

        toast({
          title: t('export.success.title', 'Exportación exitosa'),
          description: t('export.success.description', 'El archivo Excel se está descargando'),
          variant: 'default',
        });
      } else {
        throw new Error(t('export.error.noUrl', 'No se recibió URL del archivo'));
      }
    } catch (error: any) {
      toast({
        title: t('export.error.title', 'Error al exportar'),
        description: error?.message || t('export.error.unexpected', 'Ocurrió un error inesperado'),
        variant: 'destructive',
      });
    } finally {
      setIsExporting(false);
    }
  };

  return {
    exportWalletTransactions,
    isExporting,
  };
};
